import { useMemo } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import LayersIcon from '@mui/icons-material/LayersOutlined';
import AutoStoriesIcon from '@mui/icons-material/AutoStoriesOutlined';
import BugReportIcon from '@mui/icons-material/BugReportOutlined';
import { useFilteredRows } from '../hooks/useFilteredRows';
import { fmtInt } from '../lib/format';
import type { Issue } from '../types/issue';
import type { ReactNode } from 'react';
import { useT } from '../i18n';
import { cpsTicketId } from '../lib/customer-bugs';
import { isDone } from '../lib/status';

function Stat({
  icon, label, value, color, detail,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  color: string;
  detail?: ReactNode;
}) {
  return (
    <Card sx={{ flex: 1 }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 48, height: 48, borderRadius: '50%',
              bgcolor: color, color: 'white',
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary">{label}</Typography>
            <Typography variant="h4" sx={{ lineHeight: 1, mt: 0.5 }}>{value}</Typography>
            {detail && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5, fontVariantNumeric: 'tabular-nums' }}
              >
                {detail}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function StatsCards() {
  const rows = useFilteredRows();
  const t = useT();
  const counts = useMemo(() => {
    let e = 0, st = 0, b = 0;
    let bCust = 0, bInt = 0, bOpen = 0, bDone = 0;
    for (const r of rows as Issue[]) {
      const type = r['Issue Type'];
      if (type === 'Epic') e++;
      else if (type === 'Story') st++;
      else if (type === 'Bug') {
        b++;
        if (cpsTicketId(r) != null) bCust++; else bInt++;
        if (isDone(String(r['Status'] ?? ''))) bDone++; else bOpen++;
      }
    }
    return { epics: e, stories: st, bugs: b, bCust, bInt, bOpen, bDone };
  }, [rows]);

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <Stat
        icon={<LayersIcon />}
        label={t.stats.epics}
        value={fmtInt(counts.epics)}
        color="#6b5970"
      />
      <Stat
        icon={<AutoStoriesIcon />}
        label={t.stats.stories}
        value={fmtInt(counts.stories)}
        color="#2e7d32"
      />
      <Stat
        icon={<BugReportIcon />}
        label={t.stats.bugs}
        value={fmtInt(counts.bugs)}
        color="#c62828"
        detail={
          <>
            Kunde {fmtInt(counts.bCust)} · intern {fmtInt(counts.bInt)}
            <br />
            offen {fmtInt(counts.bOpen)} · done {fmtInt(counts.bDone)}
          </>
        }
      />
    </Stack>
  );
}
